import { ChevronRightIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { createExpertRouteURL } from 'applications/expert/routing/navigationGenerator';
import GearIcon from 'assets/svg/icons/gearIcon';
import LogoutIcon from 'assets/svg/icons/LogoutIcon';
import classNames from 'classnames';
import { Avatar } from 'components/atoms/Avatar';
import { AvatarSize, AvatarType } from 'components/atoms/Avatar/Avatar.enum';
import { Loader } from 'components/atoms/Loader';
import { Menu } from 'components/atoms/Menu';
import ProfileIcon from 'components/atoms/SVG/sidebarIcons/ProfileIcon.component';
import { Tooltip } from 'components/atoms/Tooltip';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import logout from 'hooks/Authentication/useLogout';
import {
  useRedirectToCustomerBillingPortal,
  useSubscriptionPlan,
} from 'hooks/Subscriptions';
import useGetCustomNavigationLink from 'hooks/useGetCustomNavigationLink';
import useResolveFileURL from 'hooks/useResolveFileURL';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { SubscriptionPlanNameEnum } from 'types/users.types';
import { isWeb } from 'utils/constants';
import { copy } from 'utils/helpers';
import { DocumentDuplicateIconOutline } from 'utils/icons';
import { UserIcon } from 'utils/icons/figmaIcons';
import {
  isSubscriptionAmbassadorPlan,
  isSubscriptionFreePlan,
  isSubscriptionMobilePlatform,
  isSubscriptionProMax12Plan,
  isSubscriptionTrialPlan,
} from 'utils/subscriptions.utils';
import PeppolActionDot from './CountBadges/PeppolActionDot.component';
import NavigationItem from './NavigationItem.component';
import { SidebarNavigationItemTypesEnum } from './types';

const UserInfoItem: FC<{ label: string; value: string; canCopy?: boolean }> = ({
  label,
  value,
  canCopy,
}) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <p className="text-xs text-primary-400">{label}</p>
      <p className="text-xs text-primary-800 font-semibold flex items-center">
        {value}
        {canCopy ? (
          <button
            className="ml-1"
            onClick={() =>
              copy(value as string).then((success) => {
                if (success) {
                  setCopied(true);
                }
              })
            }
          >
            {copied ? (
              <Tooltip content={t('copied')}>
                <CheckIcon className="w-[15px]" />
              </Tooltip>
            ) : (
              <Tooltip content={t('copy')}>
                <DocumentDuplicateIconOutline className="w-[15px]" />
              </Tooltip>
            )}
          </button>
        ) : null}
      </p>
    </div>
  );
};

const AccountMenu: FC = () => {
  const { t } = useTranslation();
  const getUrl = useGetCustomNavigationLink();

  const subscriptionPlan = useSubscriptionPlan();
  const { data: user } = useGetCurrentCustomerData();
  const accountName = user?.companyName
    ? user.companyName
    : `${user?.firstName} ${user?.lastName}`;

  const { data: picture, isLoading } = useResolveFileURL(
    user?.photoPath as string,
  );

  const { mutate, isLoading: isRedirectingToCustomerPortal } =
    useRedirectToCustomerBillingPortal();

  const shouldSeePricingMenuItem =
    subscriptionPlan &&
    (subscriptionPlan?.planInternalName === SubscriptionPlanNameEnum.trial1 ||
      subscriptionPlan?.planInternalName === SubscriptionPlanNameEnum.free ||
      ['stripe', 'stripe-otp', 'qonversion', 'ios', 'android'].includes(
        subscriptionPlan?.fromPlatform,
      )) &&
    isWeb &&
    !isSubscriptionAmbassadorPlan(subscriptionPlan.planInternalName);

  const shouldRedirectToManageSubscription =
    isSubscriptionProMax12Plan(subscriptionPlan?.planInternalName) &&
    !isSubscriptionMobilePlatform(subscriptionPlan?.fromPlatform);

  const isInSettingsPage =
    useLocation().pathname.indexOf(getUrl('/settings')) === 0;

  return (
    <Menu
      buttonProps={{ id: 'USER_MENU', className: 'w-full' }}
      position="top"
      trigger={(api) => (
        <NavigationItem
          active={api.open}
          titleContainerClassName="flex-1"
          item={{
            title: '',
            renderTitle: () =>
              isLoading ? (
                <div className="w-7 h-7 flex items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <span
                  className={classNames(
                    'flex gap-1 flex-1 w-full items-center justify-between',
                    api.open ? '!text-primary-800' : '',
                  )}
                >
                  <span className="flex-1">
                    {t('avatarmenu.account')}
                    {!isInSettingsPage && (
                      <PeppolActionDot className="align-top inline-block" />
                    )}
                  </span>
                  <span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </span>
                </span>
              ),
            Icon: picture?.url
              ? () => (
                  <div className="flex !w-full items-center justify-center">
                    <Avatar
                      type={AvatarType.circular}
                      key={picture?.url}
                      size={AvatarSize.sm}
                      src={picture?.url as string}
                    />
                  </div>
                )
              : UserIcon,
            type: SidebarNavigationItemTypesEnum.more,
          }}
        />
      )}
      contentWrapperClassName="left-[13rem] !ring-0 !rounded-xl !px-1 !w-60"
      rootClassName="w-full"
    >
      <Menu.Group className="!px-2 py-1">
        <div className="flex gap-2">
          <div>
            {picture?.url ? (
              <div className="flex mt-1 items-center justify-center">
                <Avatar
                  type={AvatarType.circular}
                  key={picture?.url}
                  size={AvatarSize.sm}
                  src={picture?.url as string}
                />
              </div>
            ) : (
              <ProfileIcon />
            )}
          </div>
          <div className="pb-2">
            <div className="text-sm font-semibold">{accountName}</div>

            {user?.VATNumber ? (
              <UserInfoItem
                label={t('settings.companyDetails.vatNumber')}
                value={user?.VATNumber}
                canCopy
              />
            ) : null}

            {user?.steuernummer ? (
              <UserInfoItem
                label={t('settings.companyDetails.steuernummer')}
                value={user?.steuernummer}
                canCopy
              />
            ) : null}
          </div>
        </div>
      </Menu.Group>
      <Menu.Group className="p-0 pt-2">
        <Menu.Link
          activeClassName="bg-primary-50"
          className="hover:!bg-primary-50 w-full"
          to={
            isWeb
              ? '/settings/personalDetails'
              : createExpertRouteURL(user?._id, '/settings/personalDetails')
          }
        >
          <span className="flex flex-1 hover:text-primary text-primary-800">
            {/* <span className="inline-block mr-2"> */}
            <GearIcon className="w-5 h-5 inline-block mr-2 align-middle" />
            {/* </span> */}
            {isWeb
              ? t('avatarmenu.settings')
              : t('avatarmenu.settings.experts')}
            {!isInSettingsPage && (
              <PeppolActionDot className="align-top inline-block" />
            )}
          </span>
        </Menu.Link>
      </Menu.Group>

      {shouldSeePricingMenuItem ? (
        shouldRedirectToManageSubscription ? (
          <Menu.Button
            className="hover:!bg-primary-50 !px-2 w-full text-sm"
            onClick={() => mutate()}
            loading={isRedirectingToCustomerPortal}
          >
            <span className="flex items-center flex-1 hover:!text-primary text-primary-800">
              <span className="inline-block mr-2">
                <UserIcon className="w-5" />
              </span>
              {t(
                isSubscriptionTrialPlan(subscriptionPlan?.planInternalName) ||
                  isSubscriptionFreePlan(subscriptionPlan?.planInternalName)
                  ? 'avatarmenu.upgrade_subscription'
                  : 'avatarmenu.billing',
              )}
            </span>
          </Menu.Button>
        ) : (
          <Menu.Link
            className="hover:!bg-primary-50 w-full"
            to="/subscriptions"
          >
            <span className="flex items-center flex-1 hover:!text-primary text-primary-800">
              <span className="inline-block mr-2">
                <UserIcon className="w-5" />
              </span>
              {t(
                isSubscriptionTrialPlan(subscriptionPlan?.planInternalName) ||
                  isSubscriptionFreePlan(subscriptionPlan?.planInternalName)
                  ? 'avatarmenu.upgrade_subscription'
                  : 'avatarmenu.billing',
              )}
            </span>
          </Menu.Link>
        )
      ) : null}

      {isWeb ? (
        <Menu.Button
          className="group mb-2 flex w-full !border-t-0 items-center rounded-md px-2 py-2 text-sm hover:!bg-primary-50 font-semibold"
          onClick={() => logout()}
        >
          <span className="flex flex-1 gap-2 items-center text-primary-800 hover:!text-primary">
            <LogoutIcon className="w-5 h-5" />
            <div id="USER_LOGOUT_BTN">{t('avatarmenu.logout')}</div>
          </span>
        </Menu.Button>
      ) : null}
    </Menu>
  );
};

export default AccountMenu;
